import React, { lazy, Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { selectBranchInfo, selectUser } from 'selectors';
import styled, { ThemeProvider } from 'styled-components';
import useTreeChanges from 'tree-changes-hook';

import { useAppSelector } from 'modules/hooks';
import theme from 'modules/theme';

import { getLogginedUserInfo, showAlert } from 'actions';

import PrivateRoute from 'components/PrivateRoute';
import PublicRoute from 'components/PublicRoute';
import SystemAlerts from 'containers/SystemAlerts';

import { BranchInfoState, UserState } from 'types';
import {
  canViewRevenueManagement,
  OTA_CALCULATOR_ROUTE,
} from 'pages/revenue-management/ota-calculator/permissions';

const HelmetCompat: any = Helmet;

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  opacity: 1 !important;
  position: relative;
  transition: opacity 0.5s;
`;

const Main = styled.main<Pick<UserState, 'isAuthenticated'>>`
  padding: 0;
`;

const CustomerDetail = lazy(() => import('pages/customer/detail'));
const Customer = lazy(() => import('pages/customer/list'));
const ReservationCheckinTodayDetail = lazy(() => import('pages/front-desk/detail/checkin_today'));
const ReservationCheckoutTodayDetail = lazy(() => import('pages/front-desk/detail/checkout_today'));
const ReservationInhouseTodayDetail = lazy(() => import('pages/front-desk/detail/inhouse_today'));
const FrontDesk = lazy(() => import('pages/front-desk/list'));
const Guest = lazy(() => import('pages/guest'));
const GuestCheckin = lazy(() => import('pages/guest/checkin'));
const GuestCheckinBookerInfo = lazy(() => import('pages/guest/checkin/BookerInfo'));
const ConfirmReservation = lazy(() => import('pages/guest/checkin/ConfirmReservation'));
const BookingConfirmation = lazy(() => import('pages/public/BookingConfirmation'));
const FinishCheckin = lazy(() => import('pages/guest/checkin/FinishCheckin'));
const Payment = lazy(() => import('pages/guest/checkin/Payment'));
const GuestCheckinPersonalId = lazy(() => import('pages/guest/checkin/PersonalId'));
const GuestCheckinSelectRoom = lazy(() => import('pages/guest/checkin/SelectRoom'));
const GuestCustomerInfo = lazy(() => import('pages/guest/GuestCustomerInfo'));
const GuestPayment = lazy(() => import('pages/guest/GuestPayment'));
const GuestRoomNumber = lazy(() => import('pages/guest/GuestRoomNumber'));
const GuestThank = lazy(() => import('pages/guest/GuestThank'));
const HouseKeeping = lazy(() => import('pages/house-keeping'));
const NightAudit = lazy(() => import('pages/night_audit'));
const StatusPayment = lazy(() => import('pages/payment/StatusPayment'));
const Report = lazy(() => import('pages/report/list'));
const Settings = lazy(() => import('pages/settings'));
const OtaCalculator = lazy(() => import('pages/revenue-management/ota-calculator'));
const Create = lazy(() => import('pages/reservation/create'));
const ReservationDetail = lazy(() => import('pages/reservation/detail'));
const Reservation = lazy(() => import('pages/reservation/list'));
const Login = lazy(() => import('routes/Login'));
const NotFound = lazy(() => import('routes/NotFound'));
const Private = lazy(() => import('routes/Private'));

function Root() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const permission = user.permission ?? {};
  const canViewSettings = permission.settings?.view ?? permission.setup?.view ?? false;
  const branchInfoSelected: BranchInfoState = useAppSelector(selectBranchInfo);
  const { changed } = useTreeChanges(user);
  const navigate = useNavigate();

  const { isAuthenticated, username } = user;

  useEffect(() => {
    if (window.localStorage.getItem('access_token')) {
      dispatch(getLogginedUserInfo());
    }
  }, [dispatch]);

  useEffect(() => {
    if (!window.localStorage.getItem('access_token') && isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (changed('isAuthenticated', true)) {
      dispatch(
        showAlert(`Hello! And welcome ${username}!`, {
          variant: 'success',
          icon: 'bell',
          timeout: 10,
        }),
      );
    }
  }, [dispatch, changed, username]);

  const reservationBreadCrum = (
    <>
      <span className="ant-breadcrumb-link" style={{ paddingRight: 8, color: 'rgba(0,0,0,.45)' }}>
        {branchInfoSelected.abbreviation}
      </span>
      /
      <span
        aria-hidden="true"
        className="ant-breadcrumb-link"
        onClick={() => {
          navigate('/reservation');
        }}
        style={{ paddingLeft: 8, cursor: 'pointer' }}
      >
        {t('common.Reservation')}
      </span>
    </>
  );

  const frontDeskBreadCrum = (
    <>
      <span className="ant-breadcrumb-link" style={{ paddingRight: 8, color: 'rgba(0,0,0,.45)' }}>
        {branchInfoSelected.abbreviation}
      </span>
      /
      <span
        aria-hidden="true"
        className="ant-breadcrumb-link"
        onClick={() => {
          navigate('/front-desk');
        }}
        style={{ paddingLeft: 8, cursor: 'pointer' }}
      >
        {t('frontDesk.Front Desk')}
      </span>
    </>
  );

  const dashboardBreadCrum = (
    <>
      <span className="ant-breadcrumb-link" style={{ paddingRight: 8, color: 'rgba(0,0,0,.45)' }}>
        {branchInfoSelected.abbreviation}
      </span>
      /
      <span
        aria-hidden="true"
        className="ant-breadcrumb-link"
        onClick={() => {
          navigate('/front-desk');
        }}
        style={{ paddingLeft: 8, cursor: 'pointer' }}
      >
        {t('common.Dashboard')}
      </span>
    </>
  );
  /*
  const deviceBreadCrum = (
    <>
      <span className="ant-breadcrumb-link" style={{ paddingRight: 8, color: 'rgba(0,0,0,.45)' }}>
        {branchInfoSelected.abbreviation}
      </span>
      /
      <span
        aria-hidden="true"
        className="ant-breadcrumb-link"
        onClick={() => {
          navigate('/front-desk');
        }}
        style={{ paddingLeft: 8, cursor: 'pointer' }}
      >
        {t('common.Device Manager')}
      </span>
    </>
  );

  const branchManagerBreadCrum = (
    <>
      <span className="ant-breadcrumb-link" style={{ paddingRight: 8, color: 'rgba(0,0,0,.45)' }}>
        {branchInfoSelected.abbreviation}
      </span>
      /
      <span
        aria-hidden="true"
        className="ant-breadcrumb-link"
        onClick={() => {
          navigate('/power-monitoring/branch-manager');
        }}
        style={{ paddingLeft: 8, cursor: 'pointer' }}
      >
        {t('branchManager.title')}
      </span>
    </>
  );
  */

  const nightAuditBreadCrum = (
    <>
      <span className="ant-breadcrumb-link" style={{ paddingRight: 8, color: 'rgba(0,0,0,.45)' }}>
        {branchInfoSelected.abbreviation}
      </span>
      /
      <span
        aria-hidden="true"
        className="ant-breadcrumb-link"
        onClick={() => {
          navigate('/night-audit');
        }}
        style={{ paddingLeft: 8, cursor: 'pointer' }}
      >
        {t('common.Night Audit')}
      </span>
    </>
  );
  const houseKeepingBreadCrum = (
    <>
      <span className="ant-breadcrumb-link" style={{ paddingRight: 8, color: 'rgba(0,0,0,.45)' }}>
        {branchInfoSelected.abbreviation}
      </span>
      /
      <span
        aria-hidden="true"
        className="ant-breadcrumb-link"
        onClick={() => {
          navigate('/house-keeping');
        }}
        style={{ paddingLeft: 8, cursor: 'pointer' }}
      >
        {t('common.House Keeping')}
      </span>
    </>
  );

  const customerBreadCrum = (
    <>
      <span className="ant-breadcrumb-link" style={{ paddingRight: 8, color: 'rgba(0,0,0,.45)' }}>
        {branchInfoSelected.abbreviation}
      </span>
      /
      <span
        aria-hidden="true"
        className="ant-breadcrumb-link"
        onClick={() => {
          navigate('/customer');
        }}
        style={{ paddingLeft: 8, cursor: 'pointer' }}
      >
        {t('common.Customer')}
      </span>
    </>
  );

  const reportBreadCrum = (
    <>
      <span className="ant-breadcrumb-link" style={{ paddingRight: 8, color: 'rgba(0,0,0,.45)' }}>
        {branchInfoSelected.abbreviation}
      </span>
      /
      <span
        aria-hidden="true"
        className="ant-breadcrumb-link"
        onClick={() => {
          navigate('/report');
        }}
        style={{ paddingLeft: 8, cursor: 'pointer' }}
      >
        {t('report.Reports')}
      </span>
    </>
  );

  const settingsBreadCrum = (
    <>
      <span className="ant-breadcrumb-link" style={{ paddingRight: 8, color: 'rgba(0,0,0,.45)' }}>
        {branchInfoSelected.abbreviation}
      </span>
      /
      <span
        aria-hidden="true"
        className="ant-breadcrumb-link"
        onClick={() => {
          navigate('/settings');
        }}
        style={{ paddingLeft: 8, cursor: 'pointer' }}
      >
        {t('common.Settings')}
      </span>
    </>
  );

  const otaCalculatorBreadCrum = (
    <>
      <span className="ant-breadcrumb-link" style={{ paddingRight: 8, color: 'rgba(0,0,0,.45)' }}>
        {branchInfoSelected.abbreviation}
      </span>
      /
      <span
        aria-hidden="true"
        className="ant-breadcrumb-link"
        onClick={() => navigate(OTA_CALCULATOR_ROUTE)}
        style={{ paddingLeft: 8, cursor: 'pointer' }}
      >
        {t('otaCalculator.title')}
      </span>
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <AppWrapper className="custom-wrapper" data-testid="app">
        <HelmetCompat
          defer={false}
          encodeSpecialCharacters
          htmlAttributes={{ lang: 'pt-br' }}
          titleAttributes={{ itemprop: 'name', lang: 'pt-br' }}
          titleTemplate={`%s | ${username}`}
        >
          <link
            href="https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,400;0,700;1,400;1,700&display=swap"
            rel="stylesheet"
          />
        </HelmetCompat>
        <Main isAuthenticated={isAuthenticated}>
          <Suspense fallback={null}>
            <Routes>
              <Route
                element={
                  <PublicRoute
                    isAuthenticated={isAuthenticated}
                    to={permission.dashboard?.view === true ? '/reservation' : '/reservation'}
                  >
                    <Login />
                  </PublicRoute>
                }
                path="/"
              />
              <Route
                element={
                  <PublicRoute isAuthenticated={isAuthenticated} to="/reservation">
                    <Login />
                  </PublicRoute>
                }
                path="/login"
              />
              <Route
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated} to="/">
                    <Private />
                  </PrivateRoute>
                }
                path="/private"
              />
              {(permission.reservation?.view === true || permission.calendar?.view === true) && (
                <>
                  <Route
                    element={
                      <PrivateRoute
                        breadCrumb={reservationBreadCrum}
                        isAuthenticated={isAuthenticated}
                        to="/"
                      >
                        <Reservation />
                      </PrivateRoute>
                    }
                    path="/reservation"
                  />
                  <Route
                    element={
                      <PrivateRoute
                        breadCrumb={reservationBreadCrum}
                        isAuthenticated={isAuthenticated}
                        to="/"
                      >
                        <ReservationDetail />
                      </PrivateRoute>
                    }
                    path="/reservation/:id"
                  />
                </>
              )}
              {permission.reservation?.create === true && (
                <Route
                  element={
                    <PrivateRoute
                      breadCrumb={reservationBreadCrum}
                      isAuthenticated={isAuthenticated}
                      to="/"
                    >
                      <Create />
                    </PrivateRoute>
                  }
                  path="/reservation/create"
                />
              )}
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={reservationBreadCrum}
                    isAuthenticated={isAuthenticated}
                    to="/"
                  >
                    <StatusPayment />
                  </PrivateRoute>
                }
                path="/status-payment/:type"
              />
              {permission.nightAudit?.view === true && (
                <Route
                  element={
                    <PrivateRoute
                      breadCrumb={nightAuditBreadCrum}
                      isAuthenticated={isAuthenticated}
                      to="/"
                    >
                      <NightAudit />
                    </PrivateRoute>
                  }
                  path="night-audit"
                />
              )}
              {permission.houseKeeping?.view === true && (
                <Route
                  element={
                    <PrivateRoute
                      breadCrumb={houseKeepingBreadCrum}
                      isAuthenticated={isAuthenticated}
                      to="/"
                    >
                      <HouseKeeping />
                    </PrivateRoute>
                  }
                  path="house-keeping"
                />
              )}
              {permission.frontDeskWalkin?.view === true && (
                <Route
                  element={
                    <PrivateRoute
                      breadCrumb={houseKeepingBreadCrum}
                      isAuthenticated={isAuthenticated}
                      to="/"
                    >
                      <HouseKeeping />
                    </PrivateRoute>
                  }
                  path="house-keeping"
                />
              )}
              {permission.frontDeskCheckin?.view === true && (
                <>
                  <Route
                    element={
                      <PrivateRoute
                        breadCrumb={frontDeskBreadCrum}
                        isAuthenticated={isAuthenticated}
                        to="/"
                      >
                        <FrontDesk />
                      </PrivateRoute>
                    }
                    path="/front-desk"
                  />
                  <Route
                    element={
                      <PrivateRoute
                        breadCrumb={frontDeskBreadCrum}
                        isAuthenticated={isAuthenticated}
                        to="/"
                      >
                        <ReservationCheckinTodayDetail />
                      </PrivateRoute>
                    }
                    path="/front-desk/checkin-today/:id"
                  />
                  <Route
                    element={
                      <PrivateRoute
                        breadCrumb={frontDeskBreadCrum}
                        isAuthenticated={isAuthenticated}
                        to="/"
                      >
                        <ReservationInhouseTodayDetail />
                      </PrivateRoute>
                    }
                    path="/front-desk/inhouse-today/:id/detail/:reservationDetailId"
                  />
                  <Route
                    element={
                      <PrivateRoute
                        breadCrumb={frontDeskBreadCrum}
                        isAuthenticated={isAuthenticated}
                        to="/"
                      >
                        <ReservationCheckoutTodayDetail />
                      </PrivateRoute>
                    }
                    path="/front-desk/checkout-today/:id/detail/:reservationDetailId"
                  />
                </>
              )}

              {/* {user.permission.dashboard?.view === true && (
              <>
                <Route
                  element={
                    <PrivateRoute
                      breadCrumb={dashboardBreadCrum}
                      isAuthenticated={isAuthenticated}
                      to="/"
                    >
                      <Dashboard />
                    </PrivateRoute>
                  }
                  path="/dashboard"
                />
                <Route
                  element={
                    <PrivateRoute
                      breadCrumb={branchManagerBreadCrum}
                      isAuthenticated={isAuthenticated}
                      to="/"
                    >
                      <Branch />
                    </PrivateRoute>
                  }
                  path="/power-monitoring/branch-manager"
                />
                <Route
                  element={
                    <PrivateRoute
                      breadCrumb={deviceBreadCrum}
                      isAuthenticated={isAuthenticated}
                      to="/"
                    >
                      <DeviceManager />
                    </PrivateRoute>
                  }
                  path="/power-monitoring/device"
                />
              </>
            )} */}
              {/* <Route
              element={
                <PrivateRoute
                  breadCrumb={frontDeskBreadCrum}
                  isAuthenticated={isAuthenticated}
                  to="/"
                >
                  <DeviceManagerCreate />
                </PrivateRoute>
              }
              path="/power-monitoring/device/create"
            /> */}

              {permission.customer?.view === true && (
                <>
                  <Route
                    element={
                      <PrivateRoute
                        breadCrumb={customerBreadCrum}
                        isAuthenticated={isAuthenticated}
                        to="/"
                      >
                        <Customer />
                      </PrivateRoute>
                    }
                    path="/customer"
                  />
                  <Route
                    element={
                      <PrivateRoute
                        breadCrumb={customerBreadCrum}
                        isAuthenticated={isAuthenticated}
                        to="/"
                      >
                        <CustomerDetail />
                      </PrivateRoute>
                    }
                    path="/customer/:id"
                  />
                </>
              )}
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={dashboardBreadCrum}
                    isAuthenticated={isAuthenticated}
                    isGuestScreen
                    to="/"
                  >
                    <Guest />
                  </PrivateRoute>
                }
                path="/guest"
              />
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={frontDeskBreadCrum}
                    isAuthenticated={isAuthenticated}
                    isGuestScreen
                    to="/"
                  >
                    <GuestCheckin />
                  </PrivateRoute>
                }
                path="/guest/checkin"
              />
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={frontDeskBreadCrum}
                    isAuthenticated={isAuthenticated}
                    isGuestScreen
                    to="/"
                  >
                    <GuestCheckinSelectRoom />
                  </PrivateRoute>
                }
                path="/guest/checkin/select-room"
              />
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={frontDeskBreadCrum}
                    isAuthenticated={isAuthenticated}
                    isGuestScreen
                    to="/"
                  >
                    <GuestCheckinPersonalId />
                  </PrivateRoute>
                }
                path="/guest/checkin/upload-personal-id"
              />
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={frontDeskBreadCrum}
                    isAuthenticated={isAuthenticated}
                    isGuestScreen
                    to="/"
                  >
                    <GuestCheckinBookerInfo />
                  </PrivateRoute>
                }
                path="/guest/checkin/booker-info"
              />
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={frontDeskBreadCrum}
                    isAuthenticated={isAuthenticated}
                    isGuestScreen
                    to="/"
                  >
                    <ConfirmReservation />
                  </PrivateRoute>
                }
                path="/guest/checkin/confirm"
              />
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={frontDeskBreadCrum}
                    isAuthenticated={isAuthenticated}
                    isGuestScreen
                    to="/"
                  >
                    <FinishCheckin />
                  </PrivateRoute>
                }
                path="/guest/checkin/finish"
              />
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={frontDeskBreadCrum}
                    isAuthenticated={isAuthenticated}
                    isGuestScreen
                    to="/"
                  >
                    <Payment />
                  </PrivateRoute>
                }
                path="/guest/checkin/payment"
              />
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={dashboardBreadCrum}
                    isAuthenticated={isAuthenticated}
                    isGuestScreen
                    to="/"
                  >
                    <GuestPayment />
                  </PrivateRoute>
                }
                path="/guest-payment"
              />
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={dashboardBreadCrum}
                    isAuthenticated={isAuthenticated}
                    isGuestScreen
                    to="/"
                  >
                    <GuestCustomerInfo />
                  </PrivateRoute>
                }
                path="/guest-customer-info"
              />
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={dashboardBreadCrum}
                    isAuthenticated={isAuthenticated}
                    isGuestScreen
                    to="/"
                  >
                    <GuestRoomNumber />
                  </PrivateRoute>
                }
                path="/guest-room-number"
              />
              <Route
                element={
                  <PrivateRoute
                    breadCrumb={dashboardBreadCrum}
                    isAuthenticated={isAuthenticated}
                    isGuestScreen
                    to="/"
                  >
                    <GuestThank />
                  </PrivateRoute>
                }
                path="/guest-thank/:type"
              />
              {permission.report?.view === true && (
                <Route
                  element={
                    <PrivateRoute
                      breadCrumb={reportBreadCrum}
                      isAuthenticated={isAuthenticated}
                      to="/"
                    >
                      <Report />
                    </PrivateRoute>
                  }
                  path="/report"
                />
              )}
              {canViewSettings === true && (
                <Route
                  element={
                    <PrivateRoute
                      breadCrumb={settingsBreadCrum}
                      isAuthenticated={isAuthenticated}
                      to="/"
                    >
                      <Settings />
                    </PrivateRoute>
                  }
                  path="/settings/*"
                />
              )}
              {canViewRevenueManagement(permission) && (
                <Route
                  element={
                    <PrivateRoute
                      breadCrumb={otaCalculatorBreadCrum}
                      isAuthenticated={isAuthenticated}
                      to="/"
                    >
                      <OtaCalculator />
                    </PrivateRoute>
                  }
                  path={OTA_CALCULATOR_ROUTE}
                />
              )}
              {/* Public booking confirmation route - no auth required */}
              <Route element={<BookingConfirmation />} path="/booking/:reservationNumber" />
              <Route element={<NotFound />} path="*" />
            </Routes>
          </Suspense>
        </Main>
        <SystemAlerts />
      </AppWrapper>
    </ThemeProvider>
  );
}

export default Root;
